import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

export interface OpcionChip<T extends string | number> {
  id: T;
  etiqueta: string;
  /** Tamaño real del grupo (dotación de ese cargo/área) — opcional porque no
   * todo consumidor tiene un conteo (ej. nombres de trabajadores sueltos). */
  conteo?: number;
}

export interface ChipsSeleccionProps<T extends string | number> {
  opciones: OpcionChip<T>[];
  seleccionados: T[];
  onCambiar: (ids: T[]) => void;
  "aria-label": string;
  /** Sólo lectura: los chips siguen mostrando qué se eligió, pero no se tocan. */
  disabled?: boolean;
  className?: string;
}

/**
 * Chips de selección múltiple del Design System, envolviendo `ui/badge`
 * (design #1399, decisión 6). Pensado para conjuntos ACOTADOS con conteo real
 * (cargos, áreas) — para búsqueda sobre listas grandes (personas) usá
 * `MultiSelect` en su lugar.
 *
 * Si `opciones` viene vacío no renderiza nada — así una empresa sin áreas no
 * muestra una sección vacía (IT4, spec: áreas son clasificador, no restrictor).
 */
export function ChipsSeleccion<T extends string | number>({
  opciones,
  seleccionados,
  onCambiar,
  "aria-label": ariaLabel,
  disabled = false,
  className,
}: ChipsSeleccionProps<T>) {
  if (opciones.length === 0) return null;

  function alternar(id: T) {
    onCambiar(
      seleccionados.includes(id)
        ? seleccionados.filter((x) => x !== id)
        : [...seleccionados, id]
    );
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn("flex flex-wrap gap-1.5", className)}
    >
      {opciones.map((op) => {
        const activo = seleccionados.includes(op.id);
        return (
          <button
            key={op.id}
            type="button"
            aria-pressed={activo}
            disabled={disabled}
            onClick={() => alternar(op.id)}
            className={cn(
              "flex h-[31px] items-center gap-1.5 rounded-full border px-[11px] text-[12.5px] transition-colors",
              activo
                ? "border-primary-border bg-primary-100 text-primary-strong"
                : "border-input bg-background text-foreground",
              disabled
                ? "cursor-default opacity-50"
                : !activo && "hover:bg-border-faint/40"
            )}
          >
            {activo && <Icon name="check" size={15} />}
            {op.etiqueta}
            {op.conteo !== undefined && (
              <span className="fc-num text-[10.5px] opacity-65">{op.conteo}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
